import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Table, Modal, Alert, Badge, Spinner, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const LugaresReunion = () => {
  const { user, loading: authLoading } = useAuth();
  const [socios, setSocios] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showFamiliaModal, setShowFamiliaModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingFamiliaId, setEditingFamiliaId] = useState(null);
  const [message, setMessage] = useState(null);
  const [activeTab, setActiveTab] = useState('socios');

  // Verificar permisos de acceso
  const canAccess = user && ['secretaria', 'admin', 'super-admin'].includes(user.role);

  // Estados para el formulario de socios
  const [formData, setFormData] = useState({
    name: '',
    lugarReunion: '',
    fecha: '',
    mes: '',
    dia: '',
    año: new Date().getFullYear(),
    horaInicio: '',
    porDefinir: false
  });

  // Estados para el formulario de familias
  const [familiaFormData, setFamiliaFormData] = useState({
    nombre: '',
    miembros: []
  });

  const [sociosDisponibles, setSociosDisponibles] = useState([]);

  // Cargar datos
  useEffect(() => {
    cargarSocios();
    cargarFamilias();
  }, []);

  // Auto-dismiss messages después de 4 segundos
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const cargarSocios = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/users?role=socio&limit=1000');
      setSocios(response.data.users || []);
      setSociosDisponibles(response.data.users || []);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error al cargar socios: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const cargarFamilias = async () => {
    try {
      const response = await axios.get('/api/familias');
      setFamilias(response.data.familias || []);
    } catch (error) {
      console.error('Error al cargar familias:', error);
      // No mostrar error si no hay familias aún
    }
  };

  // Filtrar socios que ya están en una familia DIFERENTE a la actual
  const filtrarSociosDisponibles = () => {
    if (!familias || familias.length === 0) {
      return socios;
    }

    // Obtener todos los IDs de socios ya asignados a una familia
    const sociosAsignados = new Set();
    familias.forEach(familia => {
      // Solo excluir socios de OTRAS familias, no de la actual
      if (familia._id !== editingFamiliaId) {
        familia.miembros.forEach(miembro => {
          sociosAsignados.add(miembro.userId);
        });
      }
    });

    // Retornar solo los socios que NO están asignados a OTRA familia
    return socios.filter(socio => !sociosAsignados.has(socio._id));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFamiliaInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('reunion.')) {
      const field = name.split('.')[1];
      setFamiliaFormData(prev => ({
        ...prev,
        reunion: {
          ...prev.reunion,
          [field]: value
        }
      }));
    } else {
      setFamiliaFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleMiembroChange = (userId) => {
    setFamiliaFormData(prev => {
      const miembros = [...prev.miembros];
      const index = miembros.findIndex(m => m.userId === userId);
      
      if (index > -1) {
        miembros.splice(index, 1);
      } else {
        const socio = sociosDisponibles.find(s => s._id === userId);
        miembros.push({
          userId,
          nombre: socio?.name || '',
          rol: 'miembro'
        });
      }
      
      return { ...prev, miembros };
    });
  };

  const resetFormulario = () => {
    setFormData({
      name: '',
      lugarReunion: '',
      fecha: '',
      mes: '',
      dia: '',
      año: new Date().getFullYear(),
      horaInicio: '',
      porDefinir: false
    });
    setEditingId(null);
  };

  const resetFamiliaFormulario = () => {
    setFamiliaFormData({
      nombre: '',
      miembros: []
    });
    setEditingFamiliaId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      // Validar que se haya seleccionado al menos el mes y año (a menos que esté "Por definir")
      if (!formData.porDefinir && (!formData.mes || !formData.año)) {
        setMessage({ type: 'warning', text: 'Por favor selecciona al menos el mes y el año, o marca "Por definir"' });
        setLoading(false);
        return;
      }

      // Construir la fecha con mes, día (opcional) y año
      // Importante: SIEMPRE guardar mes y año si están disponibles, incluso si porDefinir = true
      let fechaConstructida = null;
      if (formData.mes && formData.año) {
        // Si está "Por definir", usar día 15 (neutro). Si no, usar el día seleccionado o '01'
        const diaUsado = formData.porDefinir ? '15' : (formData.dia || '01');
        fechaConstructida = `${formData.año}-${String(formData.mes).padStart(2, '0')}-${String(diaUsado).padStart(2, '0')}`;
      }
      // Validar duplicados por mes+año SIEMPRE, incluso si está "Por definir"
      let duplicadoEnMesAño = false;
      
      if (formData.mes && formData.año) {
        // Obtener la familia del socio actual (si existe)
        let familiaActual = null;
        try {
          const familiaResponse = await axios.get(`/api/familias/user/${editingId}`);
          if (familiaResponse.data && familiaResponse.data.familia) {
            familiaActual = familiaResponse.data.familia;
          }
        } catch (err) {
          // No pertenece a familia, continuar
        }

        // Obtener IDs de todos los miembros de la familia actual
        const miembrosFamiliaActual = familiaActual 
          ? new Set(familiaActual.miembros.map(m => typeof m.userId === 'object' ? m.userId._id : m.userId))
          : new Set([editingId]);

        // Validar que no haya duplicados por mes+año (excepto para miembros de la misma familia)
        duplicadoEnMesAño = socios.some(socio => {
          // No verificar contra miembros de la misma familia
          if (miembrosFamiliaActual.has(socio._id)) {
            return false;
          }
          
          if (!socio.fecha) {
            return false;
          }

          const fechaSocio = new Date(socio.fecha);
          const mesSocio = fechaSocio.getMonth() + 1;
          const añoSocio = fechaSocio.getFullYear();

          // Comparar solo mes y año, no el día
          return mesSocio === formData.mes && añoSocio === formData.año;
        });
      }

      if (duplicadoEnMesAño) {
        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
        setMessage({ type: 'danger', text: `Ya existe un socio de otra familia asignado para ${meses[formData.mes - 1]} de ${formData.año}. Por favor elige otro mes o año.` });
        setLoading(false);
        return;
      }

      if (editingId) {
        // Preparar datos para actualizar - SOLO datos de reunión, no de residencia
        const updateData = {
          lugarReunion: formData.lugarReunion || null,
          fecha: fechaConstructida,
          horaInicio: formData.horaInicio || null,
          porDefinir: formData.porDefinir || false
        };

        // Actualizar el socio
        await axios.put(`/api/users/${editingId}`, updateData);

        // Verificar si el socio pertenece a una familia
        try {
          const familiaResponse = await axios.get(`/api/familias/user/${editingId}`);
          if (familiaResponse.data && familiaResponse.data.familia) {
            const familia = familiaResponse.data.familia;
            
            // Actualizar todos los miembros de la familia con la misma fecha, hora y lugar
            const actualizacionesMiembros = familia.miembros.map(miembro => {
              // Obtener el ID correctamente - si es un objeto, tomar el _id
              const miembroId = typeof miembro.userId === 'object' && miembro.userId._id 
                ? miembro.userId._id 
                : miembro.userId;
              
              return axios.put(`/api/users/${miembroId}`, {
                lugarReunion: formData.lugarReunion || null,
                fecha: fechaConstructida,
                horaInicio: formData.horaInicio || null,
                porDefinir: formData.porDefinir || false
              }).catch(err => {
                console.log('Error actualizando miembro:', err);
                return null;
              });
            });

            await Promise.all(actualizacionesMiembros);

            setMessage({ type: 'success', text: 'Lugar y horario asignados al socio y su grupo familiar' });
          } else {
            // No pertenece a familia, solo actualizar el socio
            setMessage({ type: 'success', text: 'Lugar y horario de reunión actualizado exitosamente' });
          }
        } catch (familiaError) {
          // Si hay error buscando familia, no es problema, solo actualizar socio
          setMessage({ type: 'success', text: 'Lugar y horario de reunión actualizado exitosamente' });
        }
      }

      setShowModal(false);
      resetFormulario();
      
      // Pequeño delay para asegurar que los datos se guardaron en BD
      setTimeout(async () => {
        await cargarSocios();
        await cargarFamilias();
      }, 500);
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error: ' + (error.response?.data?.message || error.message) });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitFamilia = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);

      if (!familiaFormData.nombre) {
        setMessage({ type: 'warning', text: 'Por favor ingresa el nombre de la familia' });
        setLoading(false);
        return;
      }

      if (familiaFormData.miembros.length === 0) {
        setMessage({ type: 'warning', text: 'Por favor selecciona al menos un miembro' });
        setLoading(false);
        return;
      }

      if (editingFamiliaId) {
        // Actualizar
        await axios.put(`/api/familias/${editingFamiliaId}`, familiaFormData);
        setMessage({ type: 'success', text: 'Familia actualizada exitosamente' });
      } else {
        // Crear
        await axios.post('/api/familias', familiaFormData);
        setMessage({ type: 'success', text: 'Familia creada exitosamente' });
      }

      setShowFamiliaModal(false);
      resetFamiliaFormulario();
      cargarFamilias();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error: ' + (error.response?.data?.message || error.message) });
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicion = (socio) => {
    let mes = '';
    let dia = '';
    let año = new Date().getFullYear();

    // Solo extraer fecha si el socio ya tiene una asignada
    if (socio.fecha) {
      const fecha = new Date(socio.fecha);
      mes = fecha.getMonth() + 1;
      dia = fecha.getDate();
      año = fecha.getFullYear();
    }
    
    setFormData({
      name: socio.name,
      lugarReunion: socio.lugarReunion || '',
      fecha: socio.fecha || '',
      mes: mes,
      dia: dia,
      año: año,
      horaInicio: socio.horaInicio || '',
      porDefinir: socio.porDefinir || false
    });
    setEditingId(socio._id);
    setShowModal(true);
  };

  const abrirEdicionFamilia = (familia) => {
    setFamiliaFormData({
      nombre: familia.nombre,
      miembros: familia.miembros
    });
    setEditingFamiliaId(familia._id);
    setShowFamiliaModal(true);
  };

  const abrirNuevaFamilia = () => {
    resetFamiliaFormulario();
    setShowFamiliaModal(true);
  };

  const eliminarFamilia = async (familiaId) => {
    if (!window.confirm('¿Estás seguro de que deseas eliminar esta familia?')) return;
    
    try {
      setLoading(true);
      await axios.delete(`/api/familias/${familiaId}`);
      setMessage({ type: 'success', text: 'Familia eliminada exitosamente' });
      cargarFamilias();
    } catch (error) {
      setMessage({ type: 'danger', text: 'Error al eliminar familia: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  // Mostrar spinner mientras carga
  if (authLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Row>
          <Col className="text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3 text-muted">Cargando...</p>
          </Col>
        </Row>
      </Container>
    );
  }

  // Verificar permisos de acceso
  if (!canAccess) {
    return (
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={6} className="text-center">
            <div className="card-custom p-4">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
              <h3 className="mt-3 text-danger">Acceso Denegado</h3>
              <p className="text-muted">
                No tienes permisos para acceder a esta sección. 
                Se requiere ser Secretaria o Administrador.
              </p>
              <button 
                className="btn btn-primary-custom"
                onClick={() => window.history.back()}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Volver
              </button>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4">
        <Col>
          <h2><i className="fas fa-map-marker-alt me-2"></i>Gestión de Lugares y Familias de Reunión</h2>
        </Col>
      </Row>

      {message && (
        <Alert 
          variant={message.type}
          onClose={() => setMessage(null)} 
          dismissible
          style={message.type === 'success' ? {
            backgroundColor: '#155724',
            color: '#fff',
            borderColor: '#1e7e34',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            fontWeight: '500',
            fontSize: '16px'
          } : {}}
        >
          {message.type === 'success' && <i className="fas fa-check-circle me-2"></i>}
          {message.text}
        </Alert>
      )}

      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4">
        {/* TAB 1: Lugares de Socios */}
        <Tab eventKey="socios" title={<><i className="fas fa-home me-2"></i>Lugares de Socios</>}>
          <Card className="mt-3">
            <Card.Body>
              {loading && activeTab === 'socios' ? (
                <div className="text-center"><Spinner animation="border" /></div>
              ) : socios.length === 0 ? (
                <Alert variant="info">No hay socios registrados</Alert>
              ) : (
                <div className="table-responsive">
                  <Table hover>
                    <thead className="table-header-dark">
                      <tr>
                        <th>Socio</th>
                        <th>Lugar de Reunión</th>
                        <th>Mes</th>
                        <th>Día</th>
                        <th>Año</th>
                        <th>Hora Inicio</th>
                        <th className="text-center">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {socios.map(socio => {
                        const fecha = socio.fecha ? new Date(socio.fecha) : null;
                        const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                        
                        return (
                        <tr key={socio._id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className="fas fa-user-circle fa-2x text-primary me-2"></i>
                              <div>
                                <div className="fw-bold">{socio.name}</div>
                                <small className="text-muted">{socio.email}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            {socio.lugarReunion ? (
                              <strong className="text-info">{socio.lugarReunion}</strong>
                            ) : socio.porDefinir ? (
                              <Badge bg="warning">Por definir</Badge>
                            ) : (
                              <Badge bg="warning">No configurado</Badge>
                            )}
                          </td>
                          <td>
                            {fecha ? (
                              <span>{meses[fecha.getMonth()]}</span>
                            ) : (
                              <Badge bg="secondary">Por definir</Badge>
                            )}
                          </td>
                          <td>
                            {socio.porDefinir === true || !socio.dia ? (
                              <Badge bg="warning">Por definir</Badge>
                            ) : fecha ? (
                              <span>{fecha.getDate()}</span>
                            ) : (
                              <Badge bg="secondary">Por definir</Badge>
                            )}
                          </td>
                          <td>
                            {fecha ? (
                              <span>{fecha.getFullYear()}</span>
                            ) : (
                              <Badge bg="secondary">Por definir</Badge>
                            )}
                          </td>
                          <td>
                            {socio.porDefinir === true || !socio.horaInicio || socio.horaInicio === null ? (
                              <Badge bg="warning">Por definir</Badge>
                            ) : (
                              <span className="text-success">{socio.horaInicio}</span>
                            )}
                          </td>
                          <td className="text-center">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => abrirEdicion(socio)}
                              title="Asignar lugar y horario"
                            >
                              <i className="fas fa-calendar me-1"></i>Asignar
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        {/* TAB 2: Familias de Reunión */}
        <Tab eventKey="familias" title={<><i className="fas fa-users me-2"></i>Familias de Reunión</>}>
          <Card className="mt-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>Gestión de Grupos Familiares</span>
              <Button variant="success" size="sm" onClick={abrirNuevaFamilia}>
                <i className="fas fa-plus me-2"></i>Nueva Familia
              </Button>
            </Card.Header>
            <Card.Body>
              {loading && activeTab === 'familias' ? (
                <div className="text-center"><Spinner animation="border" /></div>
              ) : familias.length === 0 ? (
                <Alert variant="info">No hay familias registradas aún</Alert>
              ) : (
                <div className="table-responsive">
                  <Table striped bordered hover>
                    <thead className="table-header-dark">
                      <tr>
                        <th>Nombre Familia</th>
                        <th>Miembros</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {familias.map(familia => (
                        <tr key={familia._id}>
                          <td>
                            <strong>{familia.nombre}</strong>
                          </td>
                          <td>
                            <Badge bg="info">
                              {familia.miembros.length} miembro{familia.miembros.length !== 1 ? 's' : ''}
                            </Badge>
                            <div style={{ fontSize: '0.85rem', marginTop: '5px' }}>
                              {familia.miembros.map(m => (
                                <div key={m.userId}>{m.nombre}</div>
                              ))}
                            </div>
                          </td>
                          <td>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => abrirEdicionFamilia(familia)}
                              className="me-2"
                            >
                              <i className="fas fa-edit"></i>
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => eliminarFamilia(familia._id)}
                            >
                              <i className="fas fa-trash"></i>
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Modal de edición de socios */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingId ? 'Asignar Lugar y Horario de Reunión' : 'Nuevo Lugar de Reunión'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Socio</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                disabled
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Lugar de Reunión</Form.Label>
              <Form.Control
                type="text"
                name="lugarReunion"
                placeholder="Ej: Calle Principal 123, Domicilio, Sede, etc."
                value={formData.lugarReunion}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Ingresa la dirección o lugar donde se reunirá este socio (opcional)
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                id="porDefinirCheck"
                label="Esta reunión está por definir (mostrar 'Por definir' en la tabla)"
                checked={formData.porDefinir || false}
                onChange={(e) => {
                  setFormData(prev => ({
                    ...prev,
                    porDefinir: e.target.checked,
                    dia: e.target.checked ? '' : prev.dia,
                    horaInicio: e.target.checked ? '' : prev.horaInicio
                  }));
                }}
              />
            </Form.Group>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Mes de Reunión *</Form.Label>
                  <Form.Select
                    name="mes"
                    value={formData.mes || ''}
                    onChange={(e) => {
                      const mes = parseInt(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        mes: mes
                      }));
                    }}
                  >
                    <option value="">-- Selecciona mes --</option>
                    {['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'].map((mes, idx) => (
                      <option key={idx + 1} value={idx + 1}>{mes}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Día de Reunión</Form.Label>
                  <Form.Select
                    name="dia"
                    value={formData.dia || ''}
                    onChange={(e) => {
                      const dia = e.target.value ? parseInt(e.target.value) : '';
                      setFormData(prev => ({
                        ...prev,
                        dia: dia
                      }));
                    }}
                  >
                    <option value="">-- Sin especificar --</option>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(dia => (
                      <option key={dia} value={dia}>{dia}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Por defecto vacío (opcional)
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Año de Reunión *</Form.Label>
                  <Form.Select
                    name="año"
                    value={formData.año || new Date().getFullYear()}
                    onChange={(e) => {
                      const año = parseInt(e.target.value);
                      setFormData(prev => ({
                        ...prev,
                        año: año
                      }));
                    }}
                  >
                    {[2024, 2025, 2026, 2027, 2028].map(año => (
                      <option key={año} value={año}>{año}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Hora Inicio</Form.Label>
              <Form.Control
                type="time"
                name="horaInicio"
                value={formData.horaInicio}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Hora en que inicia la reunión (opcional)
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      {/* Modal de familias */}
      <Modal show={showFamiliaModal} onHide={() => setShowFamiliaModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingFamiliaId ? 'Editar Familia' : 'Nueva Familia'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmitFamilia}>
            <Form.Group className="mb-3">
              <Form.Label>Nombre de la Familia *</Form.Label>
              <Form.Control
                type="text"
                name="nombre"
                placeholder="Ej: Familia García, Grupo Norte, etc."
                value={familiaFormData.nombre}
                onChange={handleFamiliaInputChange}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Selecciona Miembros *</Form.Label>
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px', borderRadius: '4px' }}>
                {filtrarSociosDisponibles().length === 0 ? (
                  <p className="text-muted text-center mb-0">Todos los socios ya están asignados a una familia</p>
                ) : (
                  filtrarSociosDisponibles().map(socio => (
                    <Form.Check
                      key={socio._id}
                      type="checkbox"
                      label={socio.name}
                      checked={familiaFormData.miembros.some(m => m.userId === socio._id)}
                      onChange={() => handleMiembroChange(socio._id)}
                    />
                  ))
                )}
              </div>
              <Form.Text className="text-muted">
                Los miembros seleccionados serán registrados juntos en reuniones
              </Form.Text>
            </Form.Group>

            <div className="d-flex gap-2 justify-content-end">
              <Button variant="secondary" onClick={() => setShowFamiliaModal(false)}>
                Cancelar
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default LugaresReunion;
